import { ProductDraft } from "@/types/general";
import { useAbility } from "@/casl.config";
import BaseExportModal from "@reusables/Modals/BaseExportModal";
import {
    adaptToExportRequest,
    BaseExportModalPropsProvider,
} from "@reusables/Modals/BaseExportModal/utils";
import { useGetExportableColumnsQuery } from "@redux/features/dfs/dfsApi";
import { performExport } from "@redux/features/dfs/thunks";
import { useExportProductDraftsFileMutation } from "@redux/features/productDrafts/productDraftsApi";
import { useAppDispatch } from "@redux/hooks";

type ProductDraftOrdering = Array<{
    name: ProductDraft.DTO.OrderBy;
    type: "asc" | "desc";
}>;

type ProductDraftsExportModalProps = BaseExportModalPropsProvider<
    ProductDraft.DTO.Filters,
    ProductDraftOrdering
>;

export default function ProductDraftsExportModal({
    isOpen,
    onClose,
    ...configuration
}: ProductDraftsExportModalProps) {
    const dispatch = useAppDispatch();
    const ability = useAbility();
    const [exportProductDrafts] = useExportProductDraftsFileMutation();
    const { data: columns = [], isLoading: isColumnsLoading } = useGetExportableColumnsQuery(
        { module: "product_draft" },
        { skip: !isOpen || ability.cannot("export", "product") }
    );

    return (
        <BaseExportModal
            isOpen={isOpen}
            onClose={onClose}
            onSubmit={(data) => {
                const operation = dispatch(
                    performExport("product_draft", () =>
                        exportProductDrafts(
                            adaptToExportRequest<
                                string,
                                ProductDraft.DTO.Filters,
                                ProductDraftOrdering
                            >(configuration, data)
                        ).unwrap()
                    )
                ) as unknown as Promise<unknown>;

                // performExport already reports and logs an immediate API failure.
                void operation.catch(() => undefined);
            }}
        >
            <BaseExportModal.FiltersInheritanceWarning />
            <BaseExportModal.Email />
            <BaseExportModal.ColumnsSelector columns={columns} isLoading={isColumnsLoading} />
            <BaseExportModal.ConcurrencyWarning />
            <BaseExportModal.Confirm />
        </BaseExportModal>
    );
}
